import { useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Package, Factory, AlertTriangle, IndianRupee, ArrowDownToLine, ArrowUpFromLine, ShoppingCart, PackageX, Wallet, CalendarDays, CalendarRange, Weight, Undo2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/Common";
import { MonthPicker, PastMonthBanner } from "@/components/MonthView";
import { TypeBadge, WarehouseBadge, MovementBadge } from "@/components/Badges";
import { useLoad } from "@/hooks/useData";
import { api } from "@/lib/api";
import { fmtINR, fmtNum, monthLabel, todayStr, dateLabel, monthStr } from "@/lib/format";

const Kpi = ({ label, value, icon: Icon, tone = "text-foreground", testid, to, delay = 0 }) => {
  const body = (
    <div className="panel p-4 sm:p-5 h-full hover:border-primary/50 transition-colors fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}<Icon className="h-4 w-4" />
      </div>
      <div className={`mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight font-mono ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const Panel = ({ title, children, action }) => (
  <div className="panel p-4 sm:p-5 fade-up">
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-base md:text-lg font-semibold tracking-tight">{title}</h2>{action}
    </div>
    {children}
  </div>
);

const ReturnsRow = () => {
  const { data } = useLoad(() => api.get("/returns-summary").then((r) => r.data).catch(() => null), []);
  if (!data) return null;
  return (
    <div className="mt-5" data-testid="dashboard-returns-row">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Returns</div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Open returns" value={fmtNum(data.open)} icon={Undo2} tone="text-amber-600" testid="kpi-returns-open" to="/returns/queue" />
        <Kpi label="Unmatched" value={fmtNum(data.unmatched)} icon={Undo2} tone="text-red-600" testid="kpi-returns-unmatched" to="/returns/queue" />
        <Kpi label="Awaiting refund ₹" value={fmtINR(data.refund_pending)} icon={IndianRupee} testid="kpi-returns-refund" to="/returns/queue" />
        <Kpi label="Claims open ₹" value={fmtINR(data.claims_open)} icon={IndianRupee} tone="text-purple-700" testid="kpi-returns-claims" to="/returns/queue" />
      </div>
    </div>
  );
};

const PurchaseValues = ({ p, month, isCurrent }) => (
  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="purchase-values">
    <Kpi label={isCurrent ? "Purchase value today" : "Purchase value (day)"} value={isCurrent ? fmtINR(p.day) : "—"} icon={Wallet} tone="text-emerald-600" testid="kpi-purchase-day" to="/daily-entry" delay={320} />
    <Kpi label={`Purchase value · ${monthLabel(month)}`} value={fmtINR(p.month)} icon={CalendarDays} tone="text-emerald-600" testid="kpi-purchase-month" delay={360} />
    <Kpi label={`Purchase value · ${p.fy_label}`} value={fmtINR(p.year)} icon={CalendarRange} tone="text-emerald-600" testid="kpi-purchase-year" delay={400} />
  </div>
);

const fmtKg = (n) => `${fmtNum(n)} kg`;

const WeightCard = ({ label, inKg, outKg, testid, delay }) => (
  <div className="panel p-4 sm:p-5 fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}<Weight className="h-4 w-4" /></div>
    <div className="mt-3 grid grid-cols-2 gap-3">
      <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stock IN</div>
        <div className="text-xl sm:text-2xl font-extrabold font-mono text-emerald-600" data-testid={`${testid}-in`}>{inKg}</div></div>
      <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stock OUT</div>
        <div className="text-xl sm:text-2xl font-extrabold font-mono text-red-500" data-testid={`${testid}-out`}>{outKg}</div></div>
    </div>
  </div>
);

const WeightMoved = ({ w, month, isCurrent }) => (
  <div className="mt-3" data-testid="weight-moved">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <WeightCard label={isCurrent ? "Weight today" : "Weight (day)"} inKg={isCurrent ? fmtKg(w.in.day) : "—"} outKg={isCurrent ? fmtKg(w.out.day) : "—"} testid="kpi-weight-day" delay={440} />
      <WeightCard label={`Weight · ${monthLabel(month)}`} inKg={fmtKg(w.in.month)} outKg={fmtKg(w.out.month)} testid="kpi-weight-month" delay={480} />
      <WeightCard label={`Weight · ${w.fy_label}`} inKg={fmtKg(w.in.year)} outKg={fmtKg(w.out.year)} testid="kpi-weight-year" delay={520} />
    </div>
    {w.missing_weight > 0 && (
      <Link to="/products" className="mt-2 inline-block text-xs text-amber-700 hover:underline" data-testid="missing-weight-link">
        {w.missing_weight} products have no weight yet; they count as 0 kg. Add weights in Product Master →
      </Link>
    )}
  </div>
);

export default function Dashboard() {
  const [month, setMonth] = useState(monthStr());
  const { data } = useLoad(() => api.get("/report/dashboard", { params: { today: todayStr(), month } }).then((r) => r.data), [month]);
  const { isAdmin, can } = useAuth();
  if (!data) return <Loading />;
  const k = data.kpis;
  const trend = data.trend.map((t) => ({ ...t, label: monthLabel(t.month) }));
  return (
    <div>
      <PageHeader title="Dashboard" subtitle={data.is_current ? `Live position for ${monthLabel(data.month)} · today ${dateLabel(data.today)}` : `Month-end position for ${monthLabel(data.month)}`}>
        <MonthPicker month={month} setMonth={setMonth} />
      </PageHeader>
      <PastMonthBanner data={data} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Total SKUs" value={fmtNum(k.total_skus)} icon={Package} testid="kpi-total-skus" to="/products" />
        <Kpi label="Products to produce" value={fmtNum(k.products_to_produce)} icon={Factory} tone="text-primary" testid="kpi-products-to-produce" to="/reorder" delay={40} />
        <Kpi label="Dead stock items" value={fmtNum(k.dead_stock_items)} icon={AlertTriangle} tone="text-amber-300" testid="kpi-dead-stock" to="/dead-stock" delay={80} />
        <Kpi label="Stock value" value={fmtINR(k.stock_value)} icon={IndianRupee} testid="kpi-stock-value" delay={120} />
        <Kpi label={data.is_current ? "Inward today" : "Inward (month)"} value={fmtNum(data.is_current ? k.today_in : k.month_in)} icon={ArrowDownToLine} tone="text-emerald-400" testid="kpi-today-in" to="/daily-entry" delay={160} />
        <Kpi label={data.is_current ? "Outward today" : "Outward (month)"} value={fmtNum(data.is_current ? k.today_out : k.month_out)} icon={ArrowUpFromLine} tone="text-red-400" testid="kpi-today-out" to="/daily-entry" delay={200} />
        <Kpi label={data.is_current ? "Open orders (SO / PO)" : "Open orders now (SO / PO)"} value={`${k.open_sales_orders} / ${k.open_purchase_orders}`} icon={ShoppingCart} testid="kpi-open-orders" to="/sales-orders" delay={240} />
        <Kpi label="Out of stock" value={fmtNum(k.out_of_stock)} icon={PackageX} tone="text-red-400" testid="kpi-out-of-stock" to="/products" delay={280} />
      </div>
      <PurchaseValues p={data.purchase} month={data.month} isCurrent={data.is_current} />
      <WeightMoved w={data.weight} month={data.month} isCurrent={data.is_current} />

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Panel title="Monthly sales (units)">
            <div className="h-64" data-testid="sales-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend}>
                  <CartesianGrid stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip cursor={{ fill: "rgba(244,20,68,0.08)" }} contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 6 }} />
                  <Bar dataKey="sales" fill="#F41444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>This month IN: <b className="font-mono text-emerald-400">{fmtNum(k.month_in)}</b></span>
              <span>This month OUT: <b className="font-mono text-red-400">{fmtNum(k.month_out)}</b></span>
              <span>Units in stock: <b className="font-mono text-foreground">{fmtNum(k.total_units)}</b></span>
              <span>Low bought SKUs: <b className="font-mono text-red-300">{k.low_bought}</b></span>
            </div>
          </Panel>
        </div>
        <Panel title="Stock movement mix">
          <div className="space-y-3">
            {[["Fast", "#10B981"], ["Medium", "#3B82F6"], ["Slow", "#F59E0B"]].map(([key, color]) => {
              const v = data.movement_split[key];
              return (
                <div key={key} data-testid={`movement-split-${key.toLowerCase()}`}>
                  <div className="flex justify-between text-xs mb-1"><MovementBadge value={key} /><span className="font-mono">{v}</span></div>
                  <div className="h-2 rounded bg-slate-100"><div className="h-2 rounded" style={{ width: `${(v / k.total_skus) * 100}%`, background: color }} /></div>
                </div>
              );
            })}
            <div className="pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
              {[["Low Bought", "text-red-300"], ["Balanced", "text-emerald-300"], ["Over Bought", "text-indigo-300"]].map(([p, c]) => (
                <div key={p}><div className={`font-mono text-xl font-bold ${c}`}>{data.position_split[p]}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p}</div></div>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <Panel title="Top reorder needs" action={<Link to="/reorder" className="text-xs text-primary hover:underline" data-testid="dashboard-view-reorder">View plan →</Link>}>
          <table className="data-table">
            <thead><tr><th>Product</th><th className="text-right">Stock</th><th className="text-right">Reorder</th></tr></thead>
            <tbody>
              {data.top_reorder.map((r) => (
                <tr key={r.id}><td className="max-w-[280px] truncate">{r.name}</td><td className="num">{r.current_stock}</td><td className="num text-primary font-bold">{r.reorder_qty}</td></tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel title="Recent entries">
          <table className="data-table">
            <thead><tr><th>Date</th><th>Product</th><th>Type</th><th>WH</th><th className="text-right">Qty</th></tr></thead>
            <tbody>
              {data.recent_movements.map((m) => (
                <tr key={m.id}><td className="font-mono text-xs">{m.date}</td><td className="max-w-[220px] truncate">{m.product_name}</td>
                  <td><TypeBadge value={m.type} /></td><td><WarehouseBadge value={m.warehouse} /></td><td className="num">{m.qty}</td></tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>

      <div className="mt-5">
        <Panel title="Stock by category">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Category</th><th className="text-right">SKUs</th><th className="text-right">Units</th><th className="text-right">Value</th></tr></thead>
              <tbody>
                {data.categories.map((c) => (
                  <tr key={c.category}><td>{c.category}</td><td className="num">{c.skus}</td><td className="num">{fmtNum(c.units)}</td><td className="num">{fmtINR(c.value)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
      {(isAdmin || can("returns", "view")) && <ReturnsRow />}
    </div>
  );
}
